import { Editor, Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { Color, FontFamily, FontSize, TextStyle } from '@tiptap/extension-text-style';
import { el } from '../state/store.js';
import { pushHistorySoon } from '../state/history.js';
import { CATEGORIES, FONTS, quoteFamily } from '../core/fonts.js';
import { DEFAULT_RUN, docToParagraphs, paragraphsToDoc } from '../core/textarea-doc.js';
import { ensureFont, uploadedFamilies } from '../scene/fonts.js';
import { builtInWeights, snapWeight } from '../core/fonts.js';
import { numRow, panel, readout, sliderRow } from '../ui/controls.js';

/* THE TEXT AREA EDITOR — a Tiptap (ProseMirror) instance over the app's own
   paragraphs/runs model.

   This was previously a hand-rolled `contenteditable`: styles were applied by
   manually wrapping the selection in <span>s, and the model was recovered by
   walking whatever DOM the browser had left behind. That approach cannot be
   made correct, because the browser owns the DOM and rewrites it on its own
   terms — every bug it produced was the same bug wearing a different hat
   (font-family silently dropped when Backspace merged two blocks; a size
   typed after a click concatenating into "100150%"; the toolbar unable to
   report what was actually under the cursor; a paste dumping arbitrary HTML
   the model had no way to represent).

   ProseMirror removes the whole class: the document is a real structure
   validated against a schema, and every edit — typing, deleting, pasting,
   dropping — arrives as a transaction against that structure rather than as a
   DOM mutation to be reverse-engineered afterwards. The schema below is
   deliberately cut down to EXACTLY what core/textarea-doc.js can represent, so
   "content the editor holds but the model can't store" is not a state that
   exists, including on paste.

   What did NOT change: the paragraphs/runs model itself (it is what's inside
   every saved project and preset, and what scene/textarea.js rasterises) and
   the renderer. Conversion between the two lives in core/textarea-doc.js,
   which is pure and separately tested. */

/* The two attributes Tiptap has no built-in for. Both mirror the shape of the
   bundled FontSize extension exactly (a global attribute with its own
   parseHTML/renderHTML), which is the documented way to extend textStyle.

   fontWeight: the app supports the full 100-900 scale, while a `bold` mark
   only means 700 — see core/textarea-doc.js for how the two are reconciled.
   lineHeight: per-paragraph, part of the document format (core/doc.js's
   migrate preserves it) even though nothing sets it in the UI today; carried
   here so an edit no longer silently destroys it the way the old serialiser
   did on every keystroke. */
/* Written as `key: function(){}` rather than ES6 method shorthand, which
   tools/undefined-refs.mjs reads as a bare identifier rather than a
   definition. `this` still binds to the extension either way, which is all
   Tiptap needs. */
const TextAreaAttrs = Extension.create({
  name: 'textAreaAttrs',
  addGlobalAttributes: function() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontWeight: {
            default: null,
            parseHTML: e => e.style.fontWeight || null,
            renderHTML: a => a.fontWeight ? { style: `font-weight: ${a.fontWeight}` } : {}
          }
        }
      },
      {
        types: ['paragraph'],
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: e => e.style.lineHeight || null,
            renderHTML: a => a.lineHeight ? { style: `line-height: ${a.lineHeight}` } : {}
          }
        }
      }
    ];
  },
  addCommands: function() {
    return {
      setFontWeight: weight => ({ chain }) => chain().setMark('textStyle', { fontWeight: String(weight) }).run(),
      unsetFontWeight: () => ({ chain }) => chain().setMark('textStyle', { fontWeight: null }).run()
    };
  }
});

function fontOptions(){
  const mine = uploadedFamilies();
  const opts = [];
  for(const cat of CATEGORIES){
    const inCat = FONTS.filter(f => f.category === cat);
    if(!inCat.length) continue;
    opts.push([null, cat]);
    for(const f of inCat) opts.push([f.family, f.family]);
  }
  if(mine.length){
    opts.push([null, 'Your fonts']);
    for(const u of mine) opts.push([u.family, u.family]);
  }
  return opts;
}

/* The full scale, as NAMES. Which of them a family actually offers is decided
   below — this is only how each number is written.

   These used to all be listed whatever was selected, on the reasoning that a
   Text Area selection can span runs in different fonts so there is no one
   family to narrow against, and that canvas degrades gracefully anyway. The
   first half is true and the second is the problem: canvas does not degrade to
   the nearest shipped weight, it SYNTHESISES the one you asked for by
   thickening the strokes itself. So picking Extra bold on a family that stops
   at Bold gave a face the designer never drew, and it did not look like the
   sample in the menu — reported as weights that "don't work".

   A selection spanning families has an intersection, which is the honest
   answer: those are the weights that will render as drawn everywhere in it. */
const WEIGHT_NAMES = {100:'Thin',200:'Extra light',300:'Light',400:'Regular',
                      500:'Medium',600:'Semibold',700:'Bold',800:'Extra bold',900:'Black'};

/* Which families the document actually uses. A mixed selection reports no
   fontFamily at all, so this is what it is narrowed against — the set the caret
   could be sitting in, rather than every family the app ships. */
function familiesInDoc(editor){
  const out = [];
  editor.state.doc.descendants(node => {
    if(!node.isText) return;
    const m = (node.marks || []).find(x => x.type.name === 'textStyle');
    out.push((m && m.attrs.fontFamily) || DEFAULT_RUN.font);
  });
  return out.length ? out : [DEFAULT_RUN.font];
}

/* Every weight shipped by every family in the selection, intersected. An
   UPLOADED family is unknown to the bundled table (builtInWeights returns
   null) — its file may hold any weights at all, so it is not allowed to
   narrow anything and the full scale stands. */
function weightsFor(families){
  let out = null;
  for(const f of families){
    const ws = builtInWeights(f);
    if(!ws) return Object.keys(WEIGHT_NAMES).map(Number);
    out = out ? out.filter(w => ws.includes(w)) : ws.slice();
  }
  return (out && out.length) ? out : Object.keys(WEIGHT_NAMES).map(Number);
}

/* Offered in the size field's dropdown. Percentages of the block's Base size,
   so 100 is "same as the body text" and the list runs either side of it. */
const SIZE_PRESETS = [50, 75, 100, 125, 150, 175, 200, 250, 300, 400];

/* One editor can be alive at a time — there is only ever one Text Area
   inspector on screen. Held at module scope so it can be torn down properly:
   a ProseMirror instance owns plugins, DOM listeners and a view, none of
   which are released just because its element was removed from the page by
   the rail's own innerHTML reset. */
let live = null;
export function destroyTextAreaEditor(){
  if(live){ live.destroy(); live = null; }
}

/**
 * @returns {HTMLElement} the "Content" panel: toolbar + editor
 */
export function textAreaContentPanel(ctx){
  const { o, rebuild } = ctx;
  const P = o.params;
  const p = panel('Content');
  const B = p.body;

  destroyTextAreaEditor();

  const bar = el('div', 'ta-toolbar');
  const host = el('div', 'inp ta-editor');
  host.style.fontFamily = quoteFamily(DEFAULT_RUN.font);

  const editor = new Editor({
    element: host,
    /* Cut down to exactly what the model can hold. Everything disabled here
       is something core/textarea-doc.js has no field for — with them off,
       the schema itself refuses the content rather than accepting it into a
       document that can't be saved. undoRedo is off because this app has its
       own history (state/history.js) covering the whole scene; two undo
       stacks bound to the same Ctrl+Z would fight over every keystroke. */
    extensions: [
      StarterKit.configure({
        heading: false, blockquote: false, code: false, codeBlock: false,
        horizontalRule: false, strike: false, link: false, hardBreak: false,
        trailingNode: false, undoRedo: false
      }),
      TextAlign.configure({ types: ['paragraph'], defaultAlignment: 'left' }),
      TextStyle, Color, FontFamily, FontSize, TextAreaAttrs
    ],
    content: paragraphsToDoc(P.paragraphs),
    onUpdate: ({ editor }) => {
      P.paragraphs = docToParagraphs(editor.getJSON());
      rebuild();
      // Debounced, not per-keystroke: pushHistory emits 'history:pushed',
      // which drives cloud autosave — a network write per character is not
      // what that's for. Matches the cadence every other field here uses.
      pushHistorySoon();
    },
    onSelectionUpdate: syncToolbar,
    onTransaction: syncToolbar
  });
  live = editor;

  /* ── toolbar ──────────────────────────────────────────────────────────── */

  const btn = (label, title, onClick) => {
    const b = el('button', 'btn btn--sm btn--ic', label);
    b.type = 'button'; b.title = title;
    // mousedown + preventDefault keeps the editor's selection intact — a
    // click would land after the button had already taken focus.
    b.addEventListener('mousedown', e => { e.preventDefault(); onClick(); });
    return b;
  };
  const sep = () => el('div','ta-sep');
  const chain = () => editor.chain().focus();

  const bBtn = btn('B', 'Bold', () => chain().toggleBold().run());
  const iBtn = btn('I', 'Italic', () => chain().toggleItalic().run()); iBtn.style.fontStyle = 'italic';
  const uBtn = btn('U', 'Underline', () => chain().toggleUnderline().run()); uBtn.style.textDecoration = 'underline';
  bar.append(bBtn, iBtn, uBtn, sep());

  const ulBtn = btn('•', 'Bullet list', () => chain().toggleBulletList().run());
  const olBtn = btn('1.', 'Numbered list', () => chain().toggleOrderedList().run());
  bar.append(ulBtn, olBtn, sep());

  const alignBtns = [
    ['⟸', 'Align left', 'left'], ['⟺', 'Align centre', 'center'],
    ['⟹', 'Align right', 'right'], ['☰', 'Justify', 'justify']
  ].map(([label, title, val]) => {
    const b = btn(label, title, () => chain().setTextAlign(val).run());
    b.dataset.align = val;
    bar.appendChild(b);
    return b;
  });
  bar.appendChild(sep());

  const famSel = el('select', 'inp');
  fontOptions().forEach(([v,t])=>{
    if(v === null){ const g = el('optgroup'); g.label = t; famSel.appendChild(g); return; }
    const op = el('option', null, t); op.value = v;
    (famSel.lastElementChild && famSel.lastElementChild.tagName==='OPTGROUP' ? famSel.lastElementChild : famSel).appendChild(op);
  });
  famSel.title = 'Typeface';
  famSel.addEventListener('change', ()=>{
    // Load the face so the editor previews in it rather than a fallback —
    // the canvas renderer gates on the same readiness check separately.
    ensureFont(famSel.value, 400, undefined, ()=>{});
    chain().setFontFamily(famSel.value).run();
  });
  bar.appendChild(famSel);

  const weightSel = el('select', 'inp');
  /* Rebuilt whenever the selection moves, because which weights exist depends
     on what is selected. The chosen value is preserved across the rebuild when
     the new family still has it, so moving the caret between two families that
     both ship Semibold does not silently drop back to Regular. */
  let weightList = [];
  const paintWeights = (families, want) => {
    const next = weightsFor(families);
    if(next.join() !== weightList.join()){
      weightList = next;
      weightSel.textContent = '';
      for(const w of next){
        const op = el('option', null, WEIGHT_NAMES[w] || String(w));
        op.value = String(w);
        weightSel.appendChild(op);
      }
    }
    /* Snapped rather than left blank: a run can legitimately carry a weight the
       family no longer offers — a document written before the family changed,
       or one whose font was swapped underneath it — and an empty select says
       nothing about what it is actually set to. */
    weightSel.value = String(next.includes(want) ? want : snapWeight(next, want));
  };
  paintWeights([DEFAULT_RUN.font], 400);
  weightSel.title = 'Weight';
  weightSel.addEventListener('change', ()=>{
    const w = +weightSel.value;
    // 700 goes through the real bold mark so this and the B button stay one
    // state rather than two that can disagree — see core/textarea-doc.js.
    if(w === 700) chain().unsetFontWeight().setBold().run();
    else if(w === 400) chain().unsetFontWeight().unsetBold().run();
    else chain().unsetBold().setFontWeight(w).run();
  });
  bar.appendChild(weightSel);

  /* A field and a preset menu in one control: sizes here are a PERCENTAGE of
     Base size, so the common picks are worth having one click away, while
     the whole point of a percentage scale is that any value between them is
     legitimate. The presets bracket 100% (the base) rather than climbing
     from it, since a run needs to be smaller than the body text as often as
     larger.

     Hand-rolled rather than a native <datalist>, which this was first built
     on and which does not work here: a browser FILTERS datalist options
     against whatever is already in the field, and the field always holds
     the current size. At the default of 180% no preset contains "180", so
     the menu opened completely empty — the presets existed and were
     unreachable. Filtering is right for an autocomplete and wrong for a
     fixed list of choices. A native datalist popup also renders outside the
     page, so nothing about it can be inspected or regression-tested; this
     menu is ordinary DOM and is covered by tools/verify.mjs. */
  const sizeWrap = el('span','ta-size-wrap');
  const sizeIn = el('input','inp ta-size');
  sizeIn.type = 'text';
  sizeIn.inputMode = 'decimal';
  sizeIn.autocomplete = 'off';
  sizeIn.title = 'Size — % of Base size. Pick a preset or type any value.';
  sizeIn.addEventListener('focus', ()=>sizeIn.select());
  sizeIn.addEventListener('change', ()=>applySize(sizeIn.value));

  const sizeMenu = el('div','ta-size-menu hidden');
  sizeMenu.setAttribute('role','listbox');
  const sizeToggle = el('button','ta-size-toggle');
  sizeToggle.type = 'button';
  sizeToggle.title = 'Size presets';
  sizeToggle.setAttribute('aria-haspopup','listbox');
  sizeToggle.setAttribute('aria-expanded','false');
  sizeToggle.innerHTML = '<svg viewBox="0 0 10 6" width="10" height="6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 1.5 5 4.5l3.5-3"/></svg>';

  const setMenuOpen = on => {
    sizeMenu.classList.toggle('hidden', !on);
    sizeToggle.setAttribute('aria-expanded', String(on));
  };
  const applySize = v => {
    const n = +v;
    if(n > 0) chain().setFontSize(n + '%').run();
    else syncToolbar();                     // nonsense input: put the real value back
  };

  for(const v of SIZE_PRESETS){
    const op = el('button','ta-size-opt', String(v));
    op.type = 'button';
    op.setAttribute('role','option');
    op.addEventListener('click', ()=>{
      sizeIn.value = String(v);
      setMenuOpen(false);
      applySize(v);
    });
    sizeMenu.appendChild(op);
  }
  sizeToggle.addEventListener('click', ()=>setMenuOpen(sizeMenu.classList.contains('hidden')));
  /* Closes on Escape, and on focus leaving the control entirely. focusout
     with a relatedTarget check rather than a document-level click listener:
     this panel is torn down and rebuilt constantly (see destroyTextAreaEditor),
     and a listener on document would outlive it. */
  sizeWrap.addEventListener('focusout', e=>{
    if(!sizeWrap.contains(e.relatedTarget)) setMenuOpen(false);
  });
  sizeWrap.addEventListener('keydown', e=>{
    if(e.key === 'Escape' && !sizeMenu.classList.contains('hidden')){
      e.stopPropagation();                  // don't let Escape reach the editor too
      setMenuOpen(false);
      sizeIn.focus();
    }
  });

  sizeWrap.append(sizeIn, sizeToggle, sizeMenu);
  bar.appendChild(sizeWrap);

  const colorIn = el('input','inp'); colorIn.type='color';
  colorIn.title = 'Colour';
  colorIn.addEventListener('input', ()=>chain().setColor(colorIn.value).run());
  bar.appendChild(colorIn);

  /* Reflects whatever is under the cursor (or across the selection), and —
     because ProseMirror applies a mark to a collapsed cursor as a "stored
     mark" for the next character typed — the toolbar is also how you set the
     style of text you are ABOUT to type. That's why nothing here demands a
     selection first, and why none of these controls reset themselves after
     use the way the old ones had to. */
  function syncToolbar(){
    if(!live) return;
    const style = editor.getAttributes('textStyle');
    bBtn.classList.toggle('on', editor.isActive('bold'));
    iBtn.classList.toggle('on', editor.isActive('italic'));
    uBtn.classList.toggle('on', editor.isActive('underline'));
    ulBtn.classList.toggle('on', editor.isActive('bulletList'));
    olBtn.classList.toggle('on', editor.isActive('orderedList'));
    for(const b of alignBtns) b.classList.toggle('on', editor.isActive({ textAlign: b.dataset.align }));

    famSel.value = style.fontFamily || DEFAULT_RUN.font;
    const w = parseInt(style.fontWeight, 10);
    const want = isFinite(w) && w ? w : (editor.isActive('bold') ? 700 : 400);
    /* getAttributes reports one family for a uniform selection and nothing for
       a mixed one — so a mixed selection is narrowed against every family the
       document uses, which is the set the caret could be sitting in. */
    paintWeights(style.fontFamily
      ? [style.fontFamily]
      : [...new Set(familiesInDoc(editor))], want);
    const size = parseFloat(style.fontSize);
    sizeIn.value = String(isFinite(size) && size > 0 ? size : DEFAULT_RUN.size);
    colorIn.value = style.color || DEFAULT_RUN.color;
  }
  syncToolbar();

  B.appendChild(bar);
  B.appendChild(host);
  B.appendChild(el('p','note','Styling applies to the selection, or to whatever you type next when nothing is selected.'));
  return p;
}

/* ── the box panel ────────────────────────────────────────────────────────── */

export function textAreaRows(body, ctx){
  const { o, rebuild, renderRight } = ctx;
  const P = o.params;

  body.appendChild(numRow('Width', P.w, .05, v=>{P.w=Math.max(.05,v); rebuild(); renderRight();}, 'm'));

  const info = o.node && o.node.userData.info;
  body.appendChild(readout([
    ['Height', (info ? info.h.toFixed(3) : '—') + ' m', 'chalk']
  ]));

  body.appendChild(numRow('Base size', P.baseSize, .005, v=>{P.baseSize=Math.max(.005,v); rebuild(); renderRight();}, 'm'));
  body.appendChild(sliderRow('Line height', P.lineHeight==null?1.3:P.lineHeight, .5, 3, .05, v=>{P.lineHeight=v; rebuild(); renderRight();}));
  body.appendChild(numRow('Padding', P.padding, .005, v=>{P.padding=Math.max(0,v); rebuild(); renderRight();}, 'm'));

  const n = el('p','note');
  n.innerHTML = 'A fixed-width, auto-height box — <b>Height</b> always fits whatever you’ve typed. Every run’s size is a percentage of <b>Base size</b>, so nudging it (or the width) scales everything together.';
  body.appendChild(n);
}
